import { useEffect } from 'react';
import PORTAL_SELECTORS from '../../../constants/portalSelectors';

/**
 * Makes Enter inside a preSave table's form behave exactly like clicking its
 * SAVE button.
 *
 * The forms already carry a `type='submit'` button, so the browser's native
 * implicit submission fires on Enter when the caret sits in a plain input. That
 * path is not equivalent to a click, and the difference is not cosmetic:
 *
 *  - A mousedown on SAVE blurs the focused cell on the way down, and those
 *    onBlur handlers are load-bearing. Sets normalizes an emptied field back to
 *    1 — skip it and `Array.from({ length: '' })` yields zero sets, so the
 *    exercise saves successfully with no sets and renders as nothing. The
 *    reps/load/%PR blur handlers set the field errors that
 *    handleSaveCalendarExercise gates on, and that gate fails silently.
 *  - handleSaveCalendarExercise has no in-flight guard and neither save button
 *    is disabled, so a held Enter POSTs once per key-repeat tick.
 *
 * So we take Enter over: preventDefault the native submission, blur first, then
 * submit the same form the button would.
 *
 * @param {React.RefObject} containerRef ref to the form (or an ancestor of it).
 * @param {boolean} enabled pass false to opt a table out.
 */
export const useEnterSavesForm = (containerRef, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      /** Shift+Enter is the picker's "create new exercise" shortcut; leave every
       * modified Enter to whoever owns it. */
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      /** IME composition commits with Enter — never a save. */
      if (e.isComposing || e.keyCode === 229) return;
      /** Auto-repeat would fire one submit per tick, and each one POSTs. */
      if (e.repeat) return;

      const el = containerRef.current;
      if (!el) return;
      const form = el.tagName === 'FORM' ? el : el.querySelector('form');
      if (!form) return;

      /** Scope to the form the caret is actually in. Day/week can have several
       * preSave tables mounted at once (a coach adds three exercises), and the
       * drag clone renders another copy of the tree — without this, one Enter
       * would submit every mounted form. */
      if (!form.contains(e.target)) return;

      /** Month view's popover owns Enter for the table it hosts: it also
       * handles focus sitting on the popover shell, outside this form, which a
       * form-scoped listener can't see. Bailing here keeps the two from both
       * firing and double-submitting. */
      if (form.closest('[data-month-edit-popover]')) return;

      const { tagName } = e.target;
      /** Preserve newlines, and let a focused button activate itself. */
      if (tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (tagName === 'BUTTON' || e.target.closest?.('button')) return;
      /** Enter inside portal'd UI belongs to that widget, not to SAVE. */
      if (e.target.closest?.(PORTAL_SELECTORS)) return;
      /** An open react-select menu means Enter picks the focused option. The
       * exercise picker portals its menu to document.body, so this has to be a
       * global query rather than one scoped to the form. */
      if (document.querySelector('[class*="__menu"]')) return;

      /** Take over from native implicit submission. */
      e.preventDefault();

      /** Blur so the cell's onBlur normalizers run, exactly as a click would.
       * Synchronous is enough: blur() dispatches through React's own document
       * listener, so the resulting setState is flushed before it returns and
       * requestSubmit() picks up the latest render's onSubmit closure. A
       * timeout/rAF instead would let the table unmount in between. */
      const active = document.activeElement;
      const shouldBlur = active
        && active !== form
        && form.contains(active)
        && typeof active.blur === 'function';
      if (shouldBlur) active.blur();

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        /** Safari < 16 has no requestSubmit. React 16 delegates at the
         * document, so a bubbling native submit still reaches onSubmit. */
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }

      /** Put the caret back. A rejected save leaves the table mounted, and
       * without this focus is stranded on <body> — losing the caret and making
       * a second Enter fail the containment check above. */
      if (shouldBlur && document.contains(active)) {
        active.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [containerRef, enabled]);
};

export default useEnterSavesForm;
